// biome-ignore-all lint/suspicious/noConsole: the default handlers log until assertInterfaceInit installs the app's
/**
 * Checks a condition, reports it when falsy, and returns the checked value.
 * Web's dev popup returns `true` instead when the developer chooses to debug.
 */
export type AssertFn = <T>(assertion: T, messageOrException: unknown, ...substitutions: unknown[]) => T | true;

export interface AssertInterface {
  assert: AssertFn;
  verify: AssertFn;
}

declare global {
  function assert<T>(assertion: T, messageOrException: unknown, ...substitutions: unknown[]): T | true;
  function verify<T>(assertion: T, messageOrException: unknown, ...substitutions: unknown[]): T | true;
}

// Browsers and React Native define `self` as the global scope; plain Node does not.
const hasSelf = 'self' in globalThis;

// default implementation could be enhanced with assertInterfaceInit
let assertInterface: AssertFn = (assertion, message, ...substitution) => {
  if (typeof console === 'object') {
    console?.warn('initialize global assert/assertInterface with assertInterfaceInit first...');
    if (!assertion) {
      console?.error(message, ...substitution);
    }
  }
  return assertion;
};
if (hasSelf && !globalThis.assert) {
  globalThis.assert = assertInterface;
}

// default implementation could be enhanced with assertInterfaceInit
let verifyInterface: AssertFn = (assertion, message, ...substitution) => {
  if (typeof console === 'object') {
    console.warn('initialize global verify/verifyInterface with assertInterfaceInit first...');
    if (!assertion) {
      console?.error(message, ...substitution);
    }
  }
  return assertion;
};
if (hasSelf && !globalThis.verify) {
  globalThis.verify = verifyInterface;
}

export const assertInterfaceInit = ({ assert, verify }: Partial<AssertInterface>): void => {
  assertInterface = assert || assertInterface;
  verifyInterface = verify || verifyInterface;
};

export const assert: AssertFn = (assertion, message, ...substitutions) =>
  assertInterface(assertion, message, ...substitutions);
export const verify: AssertFn = (assertion, message, ...substitutions) =>
  verifyInterface(assertion, message, ...substitutions);
